// kernel/useProjects.ts
import { projectKindOf } from '#lib/studio-project-view';
import { useCallback, RefObject, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { uploadImageDataToCloud } from '#lib/azure-client';
import { runProjectSuccessForensics } from '#lib/project-success/forensicsEngine';
import { buildProjectReportHtml, resolveAbsoluteUrl } from '#lib/project-success/htmlGenerator';
import { imageDataToDataURL, loadImageDataFromBlob, loadImageDataFromUrl } from '#lib/canvas-utils';
import { getDrawingLayerPayload } from '#lib/drawing-layer';
import { assignMissingRenderOrder } from '#lib/layer-render-order';
import { syncWorkspaceLayerLinks } from '#lib/workspace-layer-sync';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { resolveProjectGenerationViewMode } from '#lib/studio-project-view';
import type { ArtboardSize } from '#lib/vector-artboards';
import { deepEqual } from '@/kernel/entityMerge';
import { handleExternalStudioLayerDtoChanges, handleExternalStudioWorkspaceDtoChanges } from '@/kernel/useLayers';
import { handleExternalStudioWorkspaceVersionDtoChanges } from '@/kernel/useVersions';
import {
  areAutosavePayloadsEqual,
  applyRuntimeOverlayUsingDtoBoundary,
  type StudioProjectAutosavePayload,
  toLayerRuntimeOverlay,
  toStudioProjectAutosavePayload,
  toStudioLayerDtoFromRuntime,
  toStudioWorkspaceDtoFromRuntime,
  toWorkspaceRuntimeOverlay,
} from '#lib/studio-runtime-dto';
import {
  Project,
  ProjectMetadata,
  Workspace,
  WorkspaceVersion,
  Rect,
  Layer,
  KernelHandle,
  KernelState,
  InventoryEntityState,
  useProjectsReturnType,
  LoadMetadataRequestBody,
  ProjectSuccessAnalysis,
  ProjectSuccessLayerInfo,
  ProjectSuccessVersionInfo,
  GenerationTaskSnapshot,
  StudioWorkspaceDTO,
  StudioLayerDTO,
  StudioWorkspaceVersionDTO,
  ProjectType,
  ProjectAssetReport,
  UserAction,
  getLayerWorkspaceId,
  listProjectLayers as listProjectLayerRecords,
  listWorkspaceLayers,
} from '#types/types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const STUDIO_PROJECT_DTO_AUTOSAVE_SCOPE = 'studio_project_dto';
const STUDIO_PROJECT_DTO_AUTOSAVE_DEBOUNCE_MS = 900;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const decodeAnnotationMaskFromUrlWithPerf = async (url: string, _workspaceId: string): Promise<ImageData> => {
  const absoluteUrl = ensureAbsoluteUrl(url);

  const decodeViaFetchBlob = async (): Promise<ImageData> => {
    const retryDelaysMs = [0, 250, 800, 1600, 2500, 2500, 2500, 2500, 2500, 2500];
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retryDelaysMs.length; attempt += 1) {
      const delayMs = retryDelaysMs[attempt] ?? 0;
      if (delayMs > 0) {
        await wait(delayMs);
      }

      let fetchResponse: Response;
      try {
        fetchResponse = await fetch(absoluteUrl, {
          credentials: 'include',
          cache: 'no-store',
        });
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        continue;
      }

      if (fetchResponse.ok) {
        const blob = await fetchResponse.blob();
        return loadImageDataFromBlob(blob);
      }

      const shouldRetry =
        (fetchResponse.status === 401 ||
          fetchResponse.status === 403 ||
          fetchResponse.status === 429 ||
          fetchResponse.status >= 500) &&
        attempt < retryDelaysMs.length - 1;

      if (shouldRetry) {
        lastError = new Error(
          `Failed to fetch annotation mask (${fetchResponse.status} ${fetchResponse.statusText})`
        );
        continue;
      }

      throw new Error(`Failed to fetch annotation mask (${fetchResponse.status} ${fetchResponse.statusText})`);
    }

    if (lastError) {
      throw lastError;
    }

    throw new Error('Failed to fetch annotation mask (unknown error).');
  };

  try {
    return await decodeViaFetchBlob();
  } catch {
    return loadImageDataFromUrl(absoluteUrl);
  }
};

const sanitizeFileName = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  const cleaned = normalized.replace(/[^a-z0-9-_]+/gi, '_');
  const collapsed = cleaned.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  return collapsed.length > 0 ? collapsed : 'project';
};

const sanitizeWorkspaceForPersistence = (
  workspace: Workspace
): Omit<Workspace, 'annotationMask' | 'lastMaskUrl'> => {
  const { annotationMask, lastMaskUrl, ...rest } = workspace;
  void annotationMask;
  void lastMaskUrl;
  return rest;
};

const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read blob as data URL.'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read blob as data URL.'));
    reader.readAsDataURL(blob);
  });

const ensureAbsoluteUrl = (value: string): string => {
  const resolved = resolveAbsoluteUrl(value);
  if (resolved) {
    return resolved;
  }
  if (typeof window !== 'undefined') {
    try {
      return new URL(value, window.location.origin).href;
    } catch {
      return value;
    }
  }
  return value;
};

interface StudioHydrateResponse {
  workspaces?: StudioWorkspaceDTO[];
  layers?: StudioLayerDTO[];
  workspaceVersions?: StudioWorkspaceVersionDTO[];
}

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const toNormalizedRect = (value: unknown, fallback: Rect): Rect => {
  if (!value || typeof value !== 'object') return fallback;
  const x = toFiniteNumber(Reflect.get(value, 'x'));
  const y = toFiniteNumber(Reflect.get(value, 'y'));
  const w = toFiniteNumber(Reflect.get(value, 'w'));
  const h = toFiniteNumber(Reflect.get(value, 'h'));
  if (x === null || y === null || w === null || h === null || w <= 0 || h <= 0) {
    return fallback;
  }
  return { x, y, w, h };
};

const toNormalizedLayerRect = (value: unknown, fallback: Rect): Rect => {
  if (!value || typeof value !== 'object') return fallback;
  const x = toFiniteNumber(Reflect.get(value, 'x'));
  const y = toFiniteNumber(Reflect.get(value, 'y'));
  const w = toFiniteNumber(Reflect.get(value, 'w'));
  const h = toFiniteNumber(Reflect.get(value, 'h'));

  return {
    x: x ?? fallback.x,
    y: y ?? fallback.y,
    w: w !== null && w >= 0 ? w : fallback.w,
    h: h !== null && h >= 0 ? h : fallback.h,
  };
};

const isDiffusionParamsLike = (value: unknown): value is NonNullable<Layer['params']> =>
  isRecord(value);

const resolveHydratedLayerType = (layer: StudioLayerDTO): Layer['type'] => {
  if (
    layer.type === 'video' ||
    layer.type === 'audio' ||
    layer.type === 'svg'
  ) {
    return layer.type;
  }
  if (layer.source === 'drawing' || getDrawingLayerPayload(layer.params)) {
    return 'svg';
  }
  return undefined;
};

const toNormalizedStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];

const mapStudioHydratedSceneToRuntime = (payload: StudioHydrateResponse): {
  layers: Layer[];
  workspaces: Workspace[];
} | null => {
  const studioLayers = Array.isArray(payload.layers) ? payload.layers : [];
  const studioWorkspaces = Array.isArray(payload.workspaces) ? payload.workspaces : [];
  const studioVersions = Array.isArray(payload.workspaceVersions) ? payload.workspaceVersions : [];

  if (studioLayers.length === 0 && studioWorkspaces.length === 0) {
    return null;
  }

  const layerIdMap = new Map<string, string>();
  studioLayers.forEach((layer) => {
    layerIdMap.set(layer.id, layer.id);
  });

  const workspaceIdMap = new Map<string, string>();
  studioWorkspaces.forEach((workspace) => {
    if (typeof workspace.id === 'string' && workspace.id.trim().length > 0) {
      workspaceIdMap.set(workspace.id, workspace.id);
    }
  });

  const versionIdMap = new Map<string, string>();
  studioVersions.forEach((version) => {
    if (typeof version.id === 'string' && version.id.trim().length > 0) {
      versionIdMap.set(version.id, version.id);
    }
  });

  const defaultLayerRect: Rect = { x: 0, y: 0, w: 0, h: 0 };

  const runtimeLayers: Layer[] = studioLayers
    .map<Layer | null>((layer) => {
    const id = layerIdMap.get(layer.id) ?? layer.id;
    const rawWorkspaceId = getLayerWorkspaceId(layer);
    const workspaceLocalId = rawWorkspaceId ? (workspaceIdMap.get(rawWorkspaceId) ?? rawWorkspaceId) : null;
    const contentVersionNumeric = toFiniteNumber(layer.contentVersion);
    const normalizedScale = toFiniteNumber(layer.scale);
    const layerType = resolveHydratedLayerType(layer);
    return {
      id,
      projectId: typeof layer.projectId === 'string' ? layer.projectId.trim() : '',
      renderOrder: typeof layer.renderOrder === 'number' ? layer.renderOrder : undefined,
      'top-layer': layer.topLayer === true,
      imageSrc: layer.imageSrc || undefined,
      rect: toNormalizedLayerRect(layer.rect, defaultLayerRect),
      imageData: null,
      visible: layer.visible !== false,
      name: typeof layer.name === 'string' && layer.name.trim().length > 0 ? layer.name : `Layer ${id}`,
      locked: true,
      type: layerType,
      opacity: 1,
      scale: normalizedScale !== null && normalizedScale > 0 ? normalizedScale : undefined,
      layerWorkspaceId: workspaceLocalId,
      params: isDiffusionParamsLike(layer.params) ? layer.params : undefined,
      source: layer.source || 'unknown',
      timestamp: (() => {
        const normalized = toFiniteNumber(layer.timestamp);
        return normalized === null ? undefined : normalized;
      })(),
      contentVersion: contentVersionNumeric !== null ? contentVersionNumeric : undefined,
      videoSrc: layer.videoSrc || undefined,
      videoFramesUrls: Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : undefined,
      modificationLog: Array.isArray(layer.modificationLog) ? layer.modificationLog : undefined,
      reconstructedInputUrl: layer.reconstructedInputUrl || undefined,
    } as Layer;
  })
  .filter((layer): layer is Layer => layer !== null);

  const runtimeVersionsByWorkspaceId = new Map<string, WorkspaceVersion[]>();
  studioVersions.forEach((version) => {
    const workspaceLocalId =
      workspaceIdMap.get(version.workspaceId) ??
      (typeof version.workspaceId === 'string' ? version.workspaceId.trim() : '');
    if (!workspaceLocalId) {
      console.warn('[ProjectManager] Dropping hydrated workspace version without workspace scope.', {
        versionId: version.id,
      });
      return;
    }
    const localVersionId = versionIdMap.get(version.id) ?? version.id;
    const localParentVersionId = version.parentVersionId
      ? versionIdMap.get(version.parentVersionId) ?? version.parentVersionId
      : null;
    const timestamp = toFiniteNumber(version.timestamp) ?? Date.now();

    const runtimeStatus: WorkspaceVersion['status'] =
      version.status === 'VALIDATED' ||
      version.status === 'REJECTED' ||
      version.status === 'ARCHIVED'
        ? version.status
        : 'CANDIDATE';

    const runtimeVersion: WorkspaceVersion = {
      id: localVersionId,
      name: typeof version.name === 'string' && version.name.trim().length > 0 ? version.name : localVersionId,
      timestamp,
      layerVisibility: Array.isArray(version.layerVisibility)
        ? version.layerVisibility
            .map((entry) => {
              const layerId = layerIdMap.get(entry.id) ?? entry.id;
              if (typeof layerId !== 'string' || layerId.trim().length === 0) return null;
              return {
                id: layerId,
                visible: entry.visible !== false,
              };
            })
            .filter((entry): entry is { id: string; visible: boolean } => Boolean(entry))
        : [],
      imageUrl: version.imageUrl || undefined,
      isRenderCacheValid: version.isRenderCacheValid === true,
      renderStateHash: version.renderStateHash || undefined,
      status: runtimeStatus,
      parentId: localParentVersionId,
      rejectionReason: version.rejectionReason ?? null,
      favorite: version.favorite === true,
      annotationComments: Array.isArray(version.annotationComments)
        ? version.annotationComments
        : [],
      lastAnnotationMaskUrl:
        typeof version.lastAnnotationMaskUrl === 'string' && version.lastAnnotationMaskUrl.trim().length > 0
          ? version.lastAnnotationMaskUrl.trim()
          : undefined,
      annotationMask: null,
      annotationMaskIsDirty: false,
    };

    const existing = runtimeVersionsByWorkspaceId.get(workspaceLocalId) ?? [];
    runtimeVersionsByWorkspaceId.set(workspaceLocalId, [...existing, runtimeVersion]);
  });

  const layerIdsByWorkspaceId = new Map<string, string[]>();
  runtimeLayers.forEach((layer) => {
    if (!layer.layerWorkspaceId) return;
    const existing = layerIdsByWorkspaceId.get(layer.layerWorkspaceId) ?? [];
    existing.push(layer.id);
    layerIdsByWorkspaceId.set(layer.layerWorkspaceId, existing);
  });

  const defaultWorkspaceRect: Rect = { x: 40, y: 80, w: 720, h: 720 };
  const runtimeWorkspaces: Workspace[] = studioWorkspaces
    .map<Workspace | null>((workspace) => {
    const localWorkspaceId =
      workspaceIdMap.get(workspace.id) ??
      (typeof workspace.id === 'string' ? workspace.id.trim() : '');
    if (!localWorkspaceId) {
      console.warn('[ProjectManager] Dropping hydrated workspace without explicit scope.', {
        workspaceId: workspace.id,
      });
      return null;
    }
    const workspaceProjectId =
      typeof workspace.projectId === 'string' ? workspace.projectId.trim() : '';
    if (!workspaceProjectId) {
      console.warn('[ProjectManager] Dropping hydrated workspace without explicit projectId.', {
        workspaceId: localWorkspaceId,
      });
      return null;
    }
    const versions = runtimeVersionsByWorkspaceId.get(localWorkspaceId) ?? [];
    const fallbackLayerIds = layerIdsByWorkspaceId.get(localWorkspaceId) ?? [];
    const mappedLayerIds = Array.isArray(workspace.layerIds)
      ? workspace.layerIds
          .map((layerDbId) => {
            const mapped = layerIdMap.get(layerDbId);
            return mapped ?? layerDbId;
          })
          .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : fallbackLayerIds;
    const localValidatedVersionId = workspace.validatedVersionId
      ? versionIdMap.get(workspace.validatedVersionId) ?? workspace.validatedVersionId
      : null;
    const dtoActiveVersionCandidate = Reflect.get(
      workspace as unknown as Record<string, unknown>,
      'activeVersionId',
    );
    const dtoActiveVersionIdRaw =
      typeof dtoActiveVersionCandidate === 'string'
        ? dtoActiveVersionCandidate.trim()
        : '';
    const localActiveVersionId = dtoActiveVersionIdRaw
      ? (versionIdMap.get(dtoActiveVersionIdRaw) ?? dtoActiveVersionIdRaw)
      : localValidatedVersionId;
    const generationRect = toNormalizedRect(workspace.generationRect, defaultWorkspaceRect);
    const frameRect = toNormalizedRect(workspace.frameRect, generationRect);

    return {
      id: localWorkspaceId,
      projectId: workspaceProjectId,
      name: typeof workspace.name === 'string' && workspace.name.trim().length > 0 ? workspace.name : localWorkspaceId,
      layerIds: mappedLayerIds,
      activeVersionId: localActiveVersionId,
      generationRect,
      frameRect,
      aspectRatio: typeof workspace.aspectRatio === 'number' ? workspace.aspectRatio : null,
      lastMaskUrl: workspace.lastMaskUrl || undefined,
      annotationMask: null,
      annotationMaskVersionId: null,
      annotationsEnabled: workspace.annotationsEnabled !== false,
      annotationOpaque: workspace.annotationOpaque === true,
      expanded: true,
      locked: true,
      visible: workspace.visible !== false,
      cameraState: workspace.cameraState || undefined,
      versions,
      validatedVersionId: localValidatedVersionId,
      versionFilterMode: 'PRIMARY',
    } as Workspace;
  })
  .filter((workspace): workspace is Workspace => workspace !== null);

  return {
    layers: runtimeLayers,
    workspaces: runtimeWorkspaces,
  };
};

const normalizeWorkspaceIdentifierForLegacy = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  return '';
};

const createLegacyWorkspaceForStrictLoad = (
  projectId: string,
  workspaceId: string,
  rect: Rect
): Workspace => ({
  id: workspaceId,
  projectId,
  name: workspaceId,
  layerIds: [],
  activeVersionId: null,
  generationRect: { ...rect },
  frameRect: { ...rect },
  aspectRatio: null,
  annotationMask: null,
  annotationMaskVersionId: null,
  annotationsEnabled: true,
  annotationOpaque: false,
  expanded: true,
  locked: true,
  visible: true,
  validatedVersionId: null,
  versionFilterMode: 'PRIMARY',
});

const normalizeLegacyProjectSceneForStrictLoad = (
  projectId: string,
  projectContent: Record<string, unknown>
): { workspaces: Workspace[]; layers: Layer[] } => {
  const defaultWorkspaceRect: Rect = { x: 40, y: 80, w: 720, h: 720 };
  const workspaceById = new Map<string, Workspace>();
  const workspaceAliases = new Map<string, string>();

  const registerWorkspaceAlias = (alias: unknown, workspaceId: string): void => {
    const normalizedAlias = normalizeWorkspaceIdentifierForLegacy(alias);
    if (!normalizedAlias || workspaceAliases.has(normalizedAlias)) return;
    workspaceAliases.set(normalizedAlias, workspaceId);
  };

  const upsertWorkspace = (workspace: Workspace): Workspace => {
    workspaceById.set(workspace.id, workspace);
    registerWorkspaceAlias(workspace.id, workspace.id);
    registerWorkspaceAlias(workspace.name, workspace.id);
    return workspace;
  };

  const rawWorkspaces = Array.isArray(projectContent.workspaces) ? projectContent.workspaces : [];
  rawWorkspaces.forEach((rawWorkspace, index) => {
    const ws = isRecord(rawWorkspace) ? rawWorkspace : {};
    const workspaceId =
      normalizeWorkspaceIdentifierForLegacy(ws.id ?? ws.key) || `legacy-workspace-${index + 1}`;

    const generationRect = toNormalizedRect(
      ws.generationRect ?? ws.canvasPosition,
      defaultWorkspaceRect
    );
    const frameRect = toNormalizedRect(ws.frameRect, generationRect);
    const aspectRatio = toFiniteNumber(ws.aspectRatio);

    const workspace: Workspace = {
      ...createLegacyWorkspaceForStrictLoad(projectId, workspaceId, generationRect),
      name:
        typeof ws.name === 'string' && ws.name.trim().length > 0
          ? ws.name.trim()
          : workspaceId,
      layerIds: toNormalizedStringArray(ws.layerIds),
      activeVersionId:
        typeof ws.activeVersionId === 'string' && ws.activeVersionId.trim().length > 0
          ? ws.activeVersionId.trim()
          : null,
      generationRect,
      frameRect,
      aspectRatio: aspectRatio !== null ? aspectRatio : null,
      annotationsEnabled: ws.annotationsEnabled !== false,
      annotationOpaque: ws.annotationOpaque === true,
      expanded: ws.expanded !== undefined ? Boolean(ws.expanded) : true,
      locked: ws.locked !== undefined ? Boolean(ws.locked) : true,
      visible: ws.visible !== undefined ? Boolean(ws.visible) : true,
      cameraState: isRecord(ws.cameraState)
        ? (ws.cameraState as Workspace['cameraState'])
        : undefined,
      versions: Array.isArray(ws.versions) ? (ws.versions as Workspace['versions']) : undefined,
      validatedVersionId:
        typeof ws.validatedVersionId === 'string' && ws.validatedVersionId.trim().length > 0
          ? ws.validatedVersionId.trim()
          : null,
      versionFilterMode: ws.versionFilterMode === 'ALL' ? 'ALL' : 'PRIMARY',
      lastMaskUrl:
        typeof ws.lastMaskUrl === 'string' && ws.lastMaskUrl.trim().length > 0
          ? ws.lastMaskUrl.trim()
          : undefined,
    };

    upsertWorkspace(workspace);
    registerWorkspaceAlias(ws.key, workspace.id);
    registerWorkspaceAlias(ws.name, workspace.id);
  });

  if (workspaceById.size === 0) {
    upsertWorkspace(createLegacyWorkspaceForStrictLoad(projectId, 'legacy-workspace-1', defaultWorkspaceRect));
  }

  const deterministicFallbackWorkspaceId =
    Array.from(workspaceById.keys()).sort((left, right) => left.localeCompare(right))[0]!;

  const rawLayers = Array.isArray(projectContent.layers) ? projectContent.layers : [];
  const normalizedLayers: Layer[] = rawLayers.map((rawLayer, index) => {
    const layerRecord = isRecord(rawLayer) ? rawLayer : {};
    const normalizedLayerId = normalizeWorkspaceIdentifierForLegacy(layerRecord.id) || `legacy-layer-${index + 1}`;
    const hasWorkspaceScope =
      layerRecord.layerWorkspaceId !== undefined || layerRecord.workspaceId !== undefined;
    const workspaceCandidate = normalizeWorkspaceIdentifierForLegacy(
      layerRecord.layerWorkspaceId !== undefined ? layerRecord.layerWorkspaceId : layerRecord.workspaceId
    );
    let mappedWorkspaceId: string | null = !hasWorkspaceScope
      ? deterministicFallbackWorkspaceId
      : workspaceCandidate
        ? (workspaceAliases.get(workspaceCandidate) ?? workspaceCandidate)
        : null;
    if (mappedWorkspaceId && !workspaceById.has(mappedWorkspaceId)) {
      mappedWorkspaceId = deterministicFallbackWorkspaceId;
    }

    const rectRecord = isRecord(layerRecord.rect) ? layerRecord.rect : null;
    const rectX = toFiniteNumber(rectRecord?.x);
    const rectY = toFiniteNumber(rectRecord?.y);
    const rectW = toFiniteNumber(rectRecord?.w);
    const rectH = toFiniteNumber(rectRecord?.h);
    let rect: Rect = {
      x: rectX ?? 0,
      y: rectY ?? 0,
      w: rectW !== null && rectW > 0 ? rectW : 0,
      h: rectH !== null && rectH > 0 ? rectH : 0,
    };

    if (rect.w <= 0 || rect.h <= 0) {
      const workspace = mappedWorkspaceId ? workspaceById.get(mappedWorkspaceId) : undefined;
      const x = toFiniteNumber(layerRecord.x);
      const y = toFiniteNumber(layerRecord.y);
      if (x !== null && y !== null) {
        const w = toFiniteNumber(layerRecord.w);
        const h = toFiniteNumber(layerRecord.h);
        rect = {
          x: x - (workspace?.frameRect.x ?? 0),
          y: y - (workspace?.frameRect.y ?? 0),
          w: w !== null && w > 0 ? w : 512,
          h: h !== null && h > 0 ? h : 512,
        };
      } else {
        rect = {
          x: rect.x,
          y: rect.y,
          w: rect.w > 0 ? rect.w : 512,
          h: rect.h > 0 ? rect.h : 512,
        };
      }
    }

    return {
      ...(layerRecord as Partial<Layer>),
      id: normalizedLayerId,
      projectId,
      rect,
      layerWorkspaceId: mappedWorkspaceId,
    } as Layer;
  });

  const layerIdsByWorkspaceId = new Map<string, string[]>();
  normalizedLayers.forEach((layer) => {
    const workspaceId = normalizeWorkspaceIdentifierForLegacy(layer.layerWorkspaceId ?? '');
    const layerId = normalizeWorkspaceIdentifierForLegacy(layer.id);
    if (!workspaceId || !layerId) return;
    const current = layerIdsByWorkspaceId.get(workspaceId) ?? [];
    if (!current.includes(layerId)) {
      current.push(layerId);
      layerIdsByWorkspaceId.set(workspaceId, current);
    }
  });

  const normalizedWorkspaces = Array.from(workspaceById.values()).map((workspace) => {
    const explicitLayerIds = Array.isArray(workspace.layerIds)
      ? workspace.layerIds.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      : [];
    const discoveredLayerIds = layerIdsByWorkspaceId.get(workspace.id) ?? [];
    return {
      ...workspace,
      layerIds: Array.from(new Set([...explicitLayerIds, ...discoveredLayerIds])),
    };
  });

  return {
    workspaces: normalizedWorkspaces,
    layers: normalizedLayers,
  };
};

const fetchAssetAsDataURL = async (url: string): Promise<string | null> => {
  try {
    const absoluteUrl = ensureAbsoluteUrl(url);
    console.log(`[ProjectStory] Attempting to fetch asset: "${url}" (Resolved: "${absoluteUrl}")`);
    const response = await fetch(absoluteUrl, { credentials: 'include' });
    if (!response.ok) {
      console.error(
        `[ProjectStory] HTTP Error fetching asset ${absoluteUrl}: ${response.status} ${response.statusText}`,
      );
      return null;
    }
    const blob = await response.blob();
    return await blobToDataURL(blob);
  } catch (error) {
    console.error(`[ProjectStory] Exception fetching asset ${url}`, error);
    return null;
  }
};

const ensureCloudUrl = async (url: string, ownerId: string | null): Promise<string | null> => {
  if (!url) return null;
  if (!url.startsWith('data:')) return url;

  try {
    const imageData = await loadImageDataFromUrl(url);
    return await uploadImageDataToCloud(imageData, 'webp', new AbortController().signal, ownerId);
  } catch (e) {
    console.error("[ProjectManager] Failed to upload data URL to cloud", e);
    return null;
  }
};

const collectAnalysisAssetUrls = (analysis: ProjectSuccessAnalysis): Set<string> => {
  const urls = new Set<string>();

  const add = (value: string | null | undefined) => {
    if (typeof value !== 'string') {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed.startsWith('data:')) {
      return;
    }
    urls.add(trimmed);
  };

  const addLayerAssets = (layer: ProjectSuccessLayerInfo | null | undefined) => {
    if (!layer) {
      return;
    }
    add(layer.imageSrc);
    add(layer.reconstructedInputUrl);
    const inputAssets = layer.generation?.inputAssets;
    if (inputAssets) {
      add(inputAssets.main ?? undefined);
      add(inputAssets.mask ?? undefined);
      add(inputAssets.control ?? undefined);
      const references = inputAssets.references;
      if (Array.isArray(references)) {
        references.forEach((reference: string | null | undefined) => add(reference ?? undefined));
      }
    }
  };

  const addVersionAssets = (version: ProjectSuccessVersionInfo | null | undefined) => {
    if (!version) {
      return;
    }
    add(version.imageUrl ?? undefined);
  };

  for (const workspace of analysis.analyzedWorkspaces) {
    addVersionAssets(workspace.validatedVersion);
    addVersionAssets(workspace.initialVersion);
    for (const genealogy of workspace.layerGenealogies) {
      addLayerAssets(genealogy.layer);
      for (const step of genealogy.timeline) {
        addVersionAssets(step.version);
        addLayerAssets(step.layerState);
      }
    }
  }

  return urls;
};

// Opening a project mounts ProjectPage, whose load replaces the project's scene with the server's:
// anything written into the scene before that load ends is wiped. Subscribe before opening.
const waitForProjectLoad = (k: KernelHandle, projectId: string, timeoutMs = 30_000): Promise<void> =>
  new Promise((resolve, reject) => {
    let started = false;
    const unsubscribe = k.subscribe(() => {
      if (k.readProjectRuntimeState(projectId).isProjectLoading === true) {
        started = true;
      } else if (started) {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      }
    });
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error(`Project ${projectId} did not load within ${timeoutMs} ms.`));
    }, timeoutMs);
  });

// The project's rows a scene read changed, as the missed socket echoes would have described them;
// rows an echo wrote meanwhile were already handled by it.
const sceneRowsChangedSince = <K extends 'StudioLayer' | 'StudioWorkspace' | 'StudioWorkspaceVersion'>(
  { projectId, echoed, before, after }: {
    projectId: string;
    echoed: ReadonlySet<string>;
    before: KernelState['inventory'];
    after: KernelState['inventory'];
  },
  entityType: K
) => {
  const previous = before[entityType].registry as Record<string, InventoryEntityState<K>>;
  return (Object.values(after[entityType].registry) as InventoryEntityState<K>[])
    .filter((entity) => entity.projectId === projectId
      && !echoed.has(`${entityType}:${entity.id}`)
      && !deepEqual(previous[entity.id], entity))
    .map((entity) => ({
      entityId: entity.id,
      eventType: previous[entity.id] ? 'UPDATE' as const : 'CREATE' as const,
      previousEntity: previous[entity.id] ?? null,
      nextEntity: entity,
    }));
};

interface UseProjectsProps {
  kernel: RefObject<KernelHandle | null>;
}

export const useProjects = ({
  kernel
}: UseProjectsProps) : useProjectsReturnType => {
  const latestProjectLoadTokenRef = useRef(0);
  const shownCanvasProjectIdRef = useRef<string | null>(null);
  const studioSceneResyncRef = useRef<Promise<void> | null>(null);
  const queueProjectDtoAutosave = useCallback((projectId: string, reason: string) => {
    const k = kernel.current;
    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!k || !normalizedProjectId) return;

    k.queueAutosave<StudioProjectAutosavePayload>({
      entityType: 'Project',
      entityId: normalizedProjectId,
      scope: STUDIO_PROJECT_DTO_AUTOSAVE_SCOPE,
      reason,
      debounceMs: STUDIO_PROJECT_DTO_AUTOSAVE_DEBOUNCE_MS,
      maxRetries: 0,
      buildPayload: () => {
        const mergedProjectState = k.readProjectKernelMergedState(normalizedProjectId);
        return toStudioProjectAutosavePayload(mergedProjectState);
      },
      persist: async (payload) => {
        await k.mutateInventory(
          [{
            action: 'UPDATE',
            entity: 'Project',
            id: normalizedProjectId,
            payload,
          }],
          `Autosave Project DTO ${normalizedProjectId}`,
        );
        return true;
      },
    });
  }, [kernel]);

  const updateProjectDto = useCallback<KernelHandle['updateProjectDto']>((projectId, patch) => {
    const k = kernel.current;
    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!k || !normalizedProjectId) return;

    const normalizedPatch = toStudioProjectAutosavePayload(patch);
    if (!normalizedPatch) return;

    // Project kind is FIXED at creation: Canvas (FREEFORM/STORE_SWAP) and List
    // (LIST) generations are not interoperable, so a patch may never cross the
    // Canvas<->List boundary.
    const requestedType = (normalizedPatch as { projectType?: ProjectType }).projectType;
    if (requestedType) {
      const currentType = k.getInventory().Project.registry[normalizedProjectId]?.projectType;
      if (currentType && projectKindOf(currentType) !== projectKindOf(requestedType)) {
        console.warn('[Projects] Ignoring projectType patch that would switch the project kind.', {
          projectId: normalizedProjectId,
          currentType,
          requestedType,
        });
        return;
      }
    }

    const mergedProjectState = k.readProjectKernelMergedState(normalizedProjectId);
    const previousPayload = toStudioProjectAutosavePayload(mergedProjectState) ?? {};
    const nextPayload = {
      ...previousPayload,
      ...normalizedPatch,
    };

    if (areAutosavePayloadsEqual(previousPayload as Record<string, unknown>, nextPayload as Record<string, unknown>)) {
      return;
    }

    k.mergeEntityKernelState('Project', normalizedProjectId, normalizedPatch);
    queueProjectDtoAutosave(normalizedProjectId, 'Studio Project DTO autosave');
  }, [kernel, queueProjectDtoAutosave]);

  const getActiveSessionProjectId = useCallback((): string => {
    const k = kernel.current;
    if (!k) return '';
    const activeSessionId = k.getActiveChatSessionId();
    if (!activeSessionId) return '';
    const session = k.getInventory().ChatSession.registry[activeSessionId];
    const projectId = session ? resolveProjectIdFromChatSessionContext(session) : null;
    return typeof projectId === 'string' ? projectId.trim() : '';
  }, [kernel]);

  const listProjectWorkspaces = useCallback((projectIdInput?: string): Workspace[] => {
    const k = kernel.current;
    if (!k) return [];
    const inventory = k.getInventory();
    const projectId = (projectIdInput?.trim() || getActiveSessionProjectId() || '');
    if (!projectId) return [];
    const project = inventory.Project.registry[projectId];
    const workspaceIds = Array.isArray(project?.workspaceIds)
      ? project.workspaceIds.filter((workspaceId): workspaceId is string => typeof workspaceId === 'string' && workspaceId.trim().length > 0)
      : [];
    return workspaceIds
      .map((workspaceId) => k.getWorkspace(workspaceId))
      .filter((workspace): workspace is Workspace => workspace !== null);
  }, [kernel, getActiveSessionProjectId]);

  const listProjectLayers = useCallback((projectIdInput?: string): Layer[] => {
    const k = kernel.current;
    if (!k) return [];
    const projectId = projectIdInput?.trim() || getActiveSessionProjectId();
    if (!projectId) return [];
    const layerRegistry = Object.values(k.getLayerRegistry());
    const workspaceLayers = listProjectWorkspaces(projectId).flatMap((workspace) =>
      listWorkspaceLayers(layerRegistry, projectId, workspace.id)
    );
    const projectLayers = listProjectLayerRecords(layerRegistry, projectId);
    return Array.from(
      new Map([...workspaceLayers, ...projectLayers].map((layer) => [layer.id, layer])).values()
    );
  }, [kernel, getActiveSessionProjectId, listProjectWorkspaces]);

  const hydrateStudioSceneFromDatabase = useCallback(async (projectId: string, options?: { bootstrapLegacy?: boolean; skipRows?: ReadonlySet<string> }): Promise<{
    layers: Layer[];
    workspaces: Workspace[];
  } | null> => {
    try {
      const k = kernel.current;
      if (!k) {
        return null;
      }

      const collectIdsForEntity = async (
        entity: 'StudioWorkspace' | 'StudioLayer' | 'StudioWorkspaceVersion',
        filters: Record<string, unknown>,
        pageLimit: number
      ): Promise<string[]> => {
        const fetchPage = async (offset: number) => {
          const result = await k.queryInventory(entity, {
            filters: filters as any,
            limit: pageLimit,
            offset,
            force: true,
            ttl: 0,
            skipRows: options?.skipRows,
          });
          if (result.error) {
            throw new Error(`[${entity}] ${result.error}`);
          }
          return result;
        };

        // The first page tells us the total, so the remaining pages can all be
        // requested at once. Walking them one-at-a-time made scene hydration cost
        // one serial round trip per page — the dominant latency when opening a
        // project with a large layer/version history.
        const firstPage = await fetchPage(0);
        const firstIds = Array.isArray(firstPage.ids) ? firstPage.ids.filter(Boolean) : [];
        const collectedIds: string[] = [...firstIds];
        const total = firstPage.pagination?.total;

        if (firstIds.length > 0 && firstPage.pagination?.hasMore) {
          if (typeof total === 'number' && Number.isFinite(total)) {
            // Known total (what every inventory route returns): the page count is
            // fixed up front, so fetch the remainder concurrently.
            const offsets: number[] = [];
            // Cap at the previous safety bound of 100 pages.
            for (let offset = firstIds.length; offset < total && offsets.length < 99; offset += pageLimit) {
              offsets.push(offset);
            }
            const pages = await Promise.all(offsets.map((offset) => fetchPage(offset)));
            pages.forEach((page) => {
              const pageIds = Array.isArray(page.ids) ? page.ids.filter(Boolean) : [];
              collectedIds.push(...pageIds);
            });
          } else {
            // No total to plan against — fall back to walking pages by the actual
            // returned length, so an endpoint that only reports `hasMore` still
            // hydrates fully instead of silently stopping after one page.
            let offset = firstIds.length;
            let safetyCounter = 1;
            while (safetyCounter < 100) {
              safetyCounter += 1;
              const page = await fetchPage(offset);
              const pageIds = Array.isArray(page.ids) ? page.ids.filter(Boolean) : [];
              if (pageIds.length === 0) break;
              collectedIds.push(...pageIds);
              offset += pageIds.length;
              if (!page.pagination?.hasMore) break;
            }
          }
        }

        return Array.from(new Set(collectedIds));
      };

      const readStudioSceneFromInventory = async (): Promise<{
        layers: Layer[];
        workspaces: Workspace[];
      } | null> => {
        const [workspaceIds, layerIds, versionIds] = await Promise.all([
          collectIdsForEntity('StudioWorkspace', { projectId, sortBy: 'createdAt', sortDir: 'asc' }, 300),
          collectIdsForEntity('StudioLayer', { projectId, sortBy: 'renderOrder', sortDir: 'asc' }, 400),
          collectIdsForEntity('StudioWorkspaceVersion', { projectId, sortBy: 'createdAt', sortDir: 'asc' }, 300),
        ]);

        const inventoryState = k.getState().inventory;
        const workspaces = workspaceIds
          .map((id) => inventoryState.StudioWorkspace.registry[id])
          .filter((entry): entry is StudioWorkspaceDTO => Boolean(entry));
        const layers = layerIds
          .map((id) => inventoryState.StudioLayer.registry[id])
          .filter((entry): entry is StudioLayerDTO => Boolean(entry));
        const workspaceVersions = versionIds
          .map((id) => inventoryState.StudioWorkspaceVersion.registry[id])
          .filter((entry): entry is StudioWorkspaceVersionDTO => Boolean(entry));

        const dbPayload: StudioHydrateResponse = {
          workspaces,
          layers,
          workspaceVersions,
        };

        return mapStudioHydratedSceneToRuntime(dbPayload);
      };

      const dbFirst = await readStudioSceneFromInventory();

      if (dbFirst || options?.bootstrapLegacy === false) {
        return dbFirst;
      }

      // Bootstrap fallback for old projects not yet migrated to DB only when DB scene is empty.
      const response = await fetch(`/api/inventory/projects/${encodeURIComponent(projectId)}/studio-hydrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ syncFromFtr: true }),
      });

      if (!response.ok) {
        return null;
      }

      await response.json();

      return await readStudioSceneFromInventory();
    } catch (error) {
      // `null` dit deja « scene vide » a l'appelant. Sans cette trace, il dirait aussi
      // « la lecture a echoue » sans que rien ne permette de distinguer les deux.
      console.error(`[Studio Hydrate] Database scene read failed for project ${projectId}.`, error);
      return null;
    }
  }, [kernel]);

  // Socket.IO replays nothing a tab missed while disconnected. A row absent from the answer stays:
  // dropping it would race a creation still in flight. The read is unordered with the socket, so a row
  // echoed while it is in flight keeps the echo (a membership patch does not bump updatedAt).
  // ponytail: same upgrade path as the write-queue fallback's guard in useInventory.ts, a row version.
  const resyncOpenStudioScene = useCallback((): Promise<void> => {
    if (studioSceneResyncRef.current) return studioSceneResyncRef.current;
    const k = kernel.current;
    const projectId = k?.resolveActiveSessionProjectId() ?? '';
    const project = projectId ? k?.getState().inventory.Project.registry[projectId] : undefined;
    if (!k || !project || projectKindOf(project.projectType) !== 'canvas') return Promise.resolve();
    const watch = k.watchInventoryEchoes();
    const resync = (async () => {
      const before = k.getState().inventory;
      if (!await hydrateStudioSceneFromDatabase(projectId, { bootstrapLegacy: false, skipRows: watch.echoed })) return;
      const after = k.getState().inventory;
      const changedRows = { projectId, echoed: watch.echoed, before, after };
      handleExternalStudioLayerDtoChanges({ kernel, changes: sceneRowsChangedSince(changedRows, 'StudioLayer') });
      handleExternalStudioWorkspaceDtoChanges({ kernel, changes: sceneRowsChangedSince(changedRows, 'StudioWorkspace') });
      handleExternalStudioWorkspaceVersionDtoChanges({ kernel, changes: sceneRowsChangedSince(changedRows, 'StudioWorkspaceVersion') });
    })().finally(() => {
      watch.stop();
      studioSceneResyncRef.current = null;
    });
    studioSceneResyncRef.current = resync;
    return resync;
  }, [kernel, hydrateStudioSceneFromDatabase]);

    const deleteProject = useCallback((projectId: string) => {
    if (!kernel.current) return;
    void kernel.current.deleteEntity('Project', projectId).catch((error: any) => {
      const errorMessage = error?.message || 'Failed to delete project.';
      kernel.current?.handleError(errorMessage);
    });
  }, [kernel]);

  const copyProject = useCallback(async (sourceProjectId: string): Promise<ProjectMetadata | null> => {
    const k = kernel.current;
    const normalizedSourceProjectId = typeof sourceProjectId === 'string' ? sourceProjectId.trim() : '';
    if (!k || !normalizedSourceProjectId) return null;

    try {
      const response = await fetch(
        `/api/inventory/projects/${encodeURIComponent(normalizedSourceProjectId)}/copy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
        }
      );

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          typeof payload?.message === 'string' && payload.message.trim().length > 0
            ? payload.message
            : 'Failed to copy project.';
        throw new Error(message);
      }

      const copiedProjectId =
        typeof payload?.project?.id === 'string' ? payload.project.id.trim() : '';
      if (!copiedProjectId) {
        throw new Error('Project copy completed but response is missing the new project id.');
      }

      await k.syncEntity('Project', copiedProjectId).catch(() => undefined);
      await k.refreshInventory('Project').catch(() => undefined);

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('fater:project-copied', {
            detail: { projectId: copiedProjectId },
          })
        );
      }

      const copiedProject = k.getState().inventory.Project.registry[copiedProjectId];
      const copiedMetadata: ProjectMetadata = {
        id: copiedProjectId,
        name: copiedProject?.name || 'Untitled Project',
        imageUrl: copiedProject?.imageUrl ?? null,
        lastModified: copiedProject?.updatedAt || new Date().toISOString(),
        totalCreditsSpent: copiedProject?.totalCreditsSpent ?? 0,
        favorite: copiedProject?.favorite ?? false,
        toDelete: copiedProject?.toDelete ?? false,
        userId: copiedProject?.userId,
      };

      toast.success(`Project copied: ${copiedMetadata.name}`);
      return copiedMetadata;
    } catch (error: any) {
      const errorMessage =
        error?.message || 'Failed to copy project. Please try again.';
      console.error('[useProjects.copyProject] Error:', error);
      k.handleError(errorMessage);
      return null;
    }
  }, [kernel]);

  // Admin-only path: an impersonating admin copies the impersonated user's project
  // onto their own account, leaves impersonation, then lands on the fresh copy.
  const adminCopyProjectToSelf = useCallback(async (projectId: string): Promise<void> => {
    if (!kernel.current || !projectId) return;

    const toastId = toast.loading('Copying project to your admin account...');
    try {
      const copyResponse = await axios.post('/api/admin', {
        action: 'adminCopyProject',
        projectId,
      });
      const { newProject } = copyResponse.data;

      toast.loading('Ending impersonation session...', { id: toastId });
      await axios.post('/api/admin', { action: 'leaveImpersonation' });
      toast.success('Session ended. Opening your new project...', { id: toastId });

      if (newProject && newProject.id) {
        await kernel.current?.syncEntity('Project', newProject.id).catch(() => undefined);
        await kernel.current?.focusEntityUI('Project', newProject.id);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'An operation failed.', { id: toastId });
    }
  }, [kernel]);

  const renameProject = useCallback(async (projectId: string, newProjectName: string) => {
    if (!kernel.current) return;
    let sanitizedProjectName = newProjectName.trim().replace(/[^a-zA-Z0-9 _.-]/g, '');

    if (sanitizedProjectName === '') return;

    try {
      await kernel.current.updateEntity('Project', projectId, { name: sanitizedProjectName });

    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to rename project.';
      console.error("Error renaming project:", error);
      kernel.current?.handleError(errorMessage);
    }
  }, [kernel]);

  const openProject = useCallback((projectId: string, userId: string, quickstartPrompt?: string, imageUrls?: string[]) => {
    const k = kernel.current;
    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!k || !normalizedProjectId) return;

    const normalizedOwnerId =
      typeof userId === 'string' && userId.trim().length > 0 ? userId.trim() : undefined;
    const normalizedPrompt = typeof quickstartPrompt === 'string' ? quickstartPrompt.trim() : '';
    const normalizedImageUrls = Array.isArray(imageUrls)
      ? Array.from(
          new Set(
            imageUrls
              .map((url) => (typeof url === 'string' ? url.trim() : ''))
              .filter((url): url is string => url.length > 0)
          )
        )
      : [];

    void (async () => {
      try {
        await k.focusEntityUI('Project', normalizedProjectId);

        if (normalizedPrompt.length === 0 && normalizedImageUrls.length === 0) {
          return;
        }

        await k.syncEntity('Project', normalizedProjectId).catch(() => undefined);
        await k.loadProject(normalizedProjectId, normalizedOwnerId);

        const activeSessionId = k.getActiveChatSessionId();
        if (!activeSessionId) {
          return;
        }

        const quickstartImages = [...normalizedImageUrls];
        const baseImageUrl = quickstartImages.shift();
        if (baseImageUrl) {
          try {
            const workspaceId = k.getProjectUIWorkspaceId(normalizedProjectId);
            if (workspaceId) {
              const baseLayer = await k.addLayerFromUrl(workspaceId, baseImageUrl, {
                name: 'Base Unit',
                source: 'quickstart',
              });
              if (baseLayer?.layerWorkspaceId) {
                await k.ensureCurrentStateAsVersion(
                  baseLayer.layerWorkspaceId,
                  'Initial Quickstart Image'
                );
              }
            }
          } catch {
            // Ignore quickstart base-image load failures and continue assistant execution.
          }
        }

        const promptForExecution = normalizedPrompt.length > 0 ? normalizedPrompt : ' ';
        const projectRow = k.getInventory().Project.registry[normalizedProjectId];
        const currentProjectType = projectRow?.projectType ?? 'FREEFORM';
        // Only STORE_SWAP has the guided askAssistant quickstart; FREEFORM and
        // LIST both take the standard execute path.
        if (currentProjectType === 'STORE_SWAP') {
          await k.askAssistant(activeSessionId, promptForExecution, quickstartImages, undefined, {
            agentMode: 'director',
          });
        } else {
          await k.execute(promptForExecution, quickstartImages, {
            agentMode: 'director',
            projectId: normalizedProjectId,
          });
        }
      } catch (e) {
        console.error('[openProject] Critical failure:', e);
        k.handleError('Failed to open project.');
      }
    })();
  }, [kernel]);

  const handleProjectLoad = useCallback(async (projectId: string, p: Project) => {
    if (!kernel.current) return;
    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!normalizedProjectId) {
      throw new Error('[ProjectManager.handleProjectLoad] Missing explicit projectId for project load.');
    }

    const rawLayersToLoad = Array.isArray(p.layers) ? p.layers : [];
    const initialLayersForCriticalPath = assignMissingRenderOrder(
      rawLayersToLoad.map((layer) => ({ ...layer, imageData: null }))
    );

    kernel.current.clearAssetLoadingFailsafe();
    kernel.current.patchProjectRuntimeState(projectId, { essentialAssetsReady: false });
    kernel.current.initializeVisualCriticalPath(projectId, initialLayersForCriticalPath);

    const projectKernelPatch: Record<string, unknown> = {};
    if (typeof p.workspaceCounter === 'number' && Number.isFinite(p.workspaceCounter)) {
      projectKernelPatch.workspaceCounter = Math.max(0, Math.floor(p.workspaceCounter));
    }
    if (Array.isArray(p.cloudAssetRegistry)) {
      projectKernelPatch.cloudAssetRegistry = p.cloudAssetRegistry;
    }
    if (Array.isArray(p.actionHistory)) {
      projectKernelPatch.actionHistory = p.actionHistory;
    }
    if (Object.keys(projectKernelPatch).length > 0) {
      kernel.current.mergeEntityKernelState('Project', normalizedProjectId, projectKernelPatch);
    }
    kernel.current.setCloudAssetRegistry(p.cloudAssetRegistry || []);
    kernel.current.setActionHistory(p.actionHistory || []);
    kernel.current.setChatPrompt('');

    const normalizeWorkspaceIdentifier = (value: unknown): string => {
      if (typeof value === 'string') return value.trim();
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
      return '';
    };

    const assertWorkspaceProjectId = (workspaceInput: unknown): unknown => {
      if (!isRecord(workspaceInput)) {
        throw new Error('[ProjectManager.handleProjectLoad] Invalid workspace payload: expected object.');
      }
      const workspaceProjectId =
        typeof workspaceInput.projectId === 'string' ? workspaceInput.projectId.trim() : '';
      if (!workspaceProjectId) {
        const workspaceId = normalizeWorkspaceIdentifier(workspaceInput.id ?? workspaceInput.key) || '<unknown>';
        throw new Error(
          `[ProjectManager.handleProjectLoad] Missing explicit workspace.projectId for workspace "${workspaceId}".`
        );
      }
      if (workspaceProjectId !== normalizedProjectId) {
        const workspaceId = normalizeWorkspaceIdentifier(workspaceInput.id ?? workspaceInput.key) || '<unknown>';
        throw new Error(
          `[ProjectManager.handleProjectLoad] Workspace "${workspaceId}" projectId mismatch (expected "${normalizedProjectId}", got "${workspaceProjectId}").`
        );
      }
      return workspaceInput;
    };

    const loadAndTrackWorkspace = (workspaceInput: unknown): Workspace => {
      const loaded = kernel.current?.loadWorkspace(assertWorkspaceProjectId(workspaceInput));
      if (!loaded) {
        throw new Error('[ProjectManager.handleProjectLoad] loadWorkspace returned no workspace.');
      }
      return loaded.workspace;
    };

    let loadedWorkspaces: Workspace[] = [];
    if (p.workspaces && Array.isArray(p.workspaces)) {
      loadedWorkspaces = p.workspaces.map((workspaceInput) => loadAndTrackWorkspace(workspaceInput));
    }

    if (loadedWorkspaces.length === 0) {
      throw new Error(`[ProjectManager.handleProjectLoad] Missing explicit workspaces for project "${normalizedProjectId}".`);
    }

    const deterministicWorkspaceFallbackId = loadedWorkspaces
      .map((workspace) => workspace.id)
      .filter((workspaceId): workspaceId is string => typeof workspaceId === 'string' && workspaceId.trim().length > 0)
      .sort((left, right) => left.localeCompare(right))[0] ?? '';
    const requestedWorkspaceId = typeof p.activeLayerWorkspaceId === 'string'
      ? p.activeLayerWorkspaceId.trim()
      : '';
    const targetUIWorkspaceId = loadedWorkspaces.some((workspace) => workspace.id === requestedWorkspaceId)
      ? requestedWorkspaceId
      : deterministicWorkspaceFallbackId;
    // expanded is not persisted: every workspace would otherwise open expanded.
    loadedWorkspaces = loadedWorkspaces.map((workspace) => ({
      ...workspace,
      expanded: workspace.id === targetUIWorkspaceId,
    }));

    const validWorkspaceIds = new Set(loadedWorkspaces.map(ws => ws.id));
    const loadedLayers = rawLayersToLoad
      .map((layerInput, index) => {
        try {
          const loadedLayerResult = kernel.current?.loadLayer({
            ...(layerInput as Record<string, unknown>),
            projectId: (layerInput as Record<string, unknown>).projectId ?? normalizedProjectId,
          });
          if (!loadedLayerResult) {
            throw new Error('[ProjectManager.handleProjectLoad] loadLayer returned no layer.');
          }

          const { layer } = loadedLayerResult;
          if (layer.projectId !== normalizedProjectId) {
            throw new Error(
              `[ProjectManager.handleProjectLoad] Layer "${layer.id}" projectId mismatch (expected "${normalizedProjectId}", got "${layer.projectId}").`
            );
          }
          if (layer.layerWorkspaceId && !validWorkspaceIds.has(layer.layerWorkspaceId)) {
            throw new Error(
              `[ProjectManager.handleProjectLoad] Layer "${layer.id}" references unknown workspace "${layer.layerWorkspaceId}" in project "${normalizedProjectId}".`
            );
          }

          if (layer.contentVersion === undefined || layer.contentVersion === null) {
            return {
              ...layer,
              contentVersion: Date.now() + index,
            };
          }
          return layer;
        } catch (error) {
          console.warn('[ProjectManager] Dropping corrupted layer during project load.', {
            projectId: normalizedProjectId,
            error,
          });
          return null;
        }
      })
      .filter((layer): layer is Layer => layer !== null);
    const layersToLoad = assignMissingRenderOrder(loadedLayers);

    const canonicalSceneState = syncWorkspaceLayerLinks({
      layers: layersToLoad,
      workspaces: loadedWorkspaces,
    });

    const previousInventory = kernel.current.getState().inventory;
    const previousWorkspaceIds = Object.entries(previousInventory.StudioWorkspace.registry)
      .filter(([, workspace]) => workspace?.projectId === projectId)
      .map(([workspaceId]) => workspaceId);
    const previousLayerIds = Object.entries(previousInventory.StudioLayer.registry)
      .filter(([, layer]) => layer?.projectId === projectId)
      .map(([layerId]) => layerId);
    const runtimeWorkspaceIds = canonicalSceneState.workspaces.map((workspace) => workspace.id);
    const runtimeWorkspaceIdsForProject = Array.from(
      new Set(
        runtimeWorkspaceIds
          .map((workspaceId) => (typeof workspaceId === 'string' ? workspaceId.trim() : ''))
          .filter((workspaceId): workspaceId is string => workspaceId.length > 0)
      )
    );
    const runtimeLayerIds = canonicalSceneState.layers.map((layer) => layer.id);
    const nextWorkspaceIds = new Set(runtimeWorkspaceIds);
    const nextLayerIds = new Set(runtimeLayerIds);
    const nowIso = new Date().toISOString();

    kernel.current.setState((prevState) => ({
      ...prevState,
      inventory: {
        ...prevState.inventory,
        Project: {
          ...prevState.inventory.Project,
          registry: {
            ...prevState.inventory.Project.registry,
            ...(prevState.inventory.Project.registry[normalizedProjectId]
              ? {
                  [normalizedProjectId]: {
                    ...prevState.inventory.Project.registry[normalizedProjectId],
                    workspaceIds: runtimeWorkspaceIdsForProject,
                  },
                }
              : {}),
          },
          lastActivity: Date.now(),
        },
        StudioLayer: {
          ...prevState.inventory.StudioLayer,
          registry: canonicalSceneState.layers.reduce<Record<string, StudioLayerDTO>>((registry, layer) => {
            const existingDto = prevState.inventory.StudioLayer.registry[layer.id];
            try {
              registry[layer.id] = toStudioLayerDtoFromRuntime(
                layer,
                existingDto,
                projectId,
                nowIso
              );
            } catch (error) {
              console.error('[ProjectManager] Skipping invalid layer inventory hydration.', {
                layerId: layer.id,
                error,
              });
            }
            return registry;
          }, Object.entries(prevState.inventory.StudioLayer.registry).reduce<Record<string, StudioLayerDTO>>((registry, [layerId, layer]) => {
            if (layer?.projectId === projectId) {
              return registry;
            }
            registry[layerId] = layer;
            return registry;
          }, {})),
          lastActivity: Date.now(),
        },
        StudioWorkspace: {
          ...prevState.inventory.StudioWorkspace,
          registry: canonicalSceneState.workspaces.reduce<Record<string, StudioWorkspaceDTO>>((registry, workspace) => {
            const existingDto = prevState.inventory.StudioWorkspace.registry[workspace.id];
            registry[workspace.id] = toStudioWorkspaceDtoFromRuntime(
              workspace,
              existingDto,
              projectId,
              new Date().toISOString()
            );
            return registry;
          }, Object.entries(prevState.inventory.StudioWorkspace.registry).reduce<Record<string, StudioWorkspaceDTO>>((registry, [workspaceId, workspace]) => {
            if (workspace?.projectId === projectId) {
              return registry;
            }
            registry[workspaceId] = workspace;
            return registry;
          }, {})),
          lastActivity: Date.now(),
        },
      },
    }));

    previousWorkspaceIds.forEach((workspaceId) => {
      if (!nextWorkspaceIds.has(workspaceId)) {
        kernel.current?.removeEntityKernelState('StudioWorkspace', workspaceId);
      }
    });
    previousLayerIds.forEach((layerId) => {
      if (!nextLayerIds.has(layerId)) {
        kernel.current?.removeEntityKernelState('StudioLayer', layerId);
      }
    });

    canonicalSceneState.workspaces.forEach((workspace) => {
      const currentDraft = kernel.current?.readEntityKernelState('StudioWorkspace', workspace.id);
      const existingDto = previousInventory.StudioWorkspace.registry[workspace.id];
      const workspaceDtoSnapshot = toStudioWorkspaceDtoFromRuntime(
        workspace,
        existingDto,
        projectId,
        nowIso,
      );
      const runtimeOverlay = toWorkspaceRuntimeOverlay(workspace);
      const nextDraft = applyRuntimeOverlayUsingDtoBoundary(
        currentDraft,
        workspaceDtoSnapshot,
        runtimeOverlay,
      );
      kernel.current?.writeEntityKernelState('StudioWorkspace', workspace.id, nextDraft);
    });
    canonicalSceneState.layers.forEach((layer) => {
      const currentDraft = kernel.current?.readEntityKernelState('StudioLayer', layer.id);
      const existingDto = previousInventory.StudioLayer.registry[layer.id];
      let layerDtoSnapshot: object | null = null;
      try {
        layerDtoSnapshot = toStudioLayerDtoFromRuntime(
          layer,
          existingDto,
          projectId,
          nowIso,
        );
      } catch (error) {
        console.error('[ProjectManager] Skipping runtime overlay reconciliation for layer without DTO boundary.', {
          layerId: layer.id,
          error,
        });
      }
      if (!layerDtoSnapshot) {
        return;
      }
      const runtimeOverlay = toLayerRuntimeOverlay(layer);
      const nextDraft = applyRuntimeOverlayUsingDtoBoundary(
        currentDraft,
        layerDtoSnapshot,
        runtimeOverlay,
      );
      kernel.current?.writeEntityKernelState('StudioLayer', layer.id, nextDraft);
    });
    kernel.current.hydrateVisibleLayers(projectId);

    if (canonicalSceneState.workspacesNeedingEnsure.length > 0) {
      queueMicrotask(() => {
        const currentKernel = kernel.current;
        if (!currentKernel?.ensureCurrentStateAsVersion) return;
        canonicalSceneState.workspacesNeedingEnsure.forEach((workspaceId) => {
          void currentKernel.ensureCurrentStateAsVersion(
            workspaceId,
            'Auto ensure from project load: detached active version',
            { skipBuriedLayerCleanup: true },
          );
        });
      });
    }

    loadedWorkspaces.forEach(ws => {
      const activeVersionId =
        (typeof ws.activeVersionId === 'string' && ws.activeVersionId.trim().length > 0)
          ? ws.activeVersionId
          : ((typeof ws.validatedVersionId === 'string' && ws.validatedVersionId.trim().length > 0)
            ? ws.validatedVersionId
            : null);
      if (!activeVersionId) return;
      const activeVersion = (ws.versions ?? []).find((version) => version.id === activeVersionId);
      const annotationMaskUrl = activeVersion?.lastAnnotationMaskUrl;
      if (!annotationMaskUrl) return;

      decodeAnnotationMaskFromUrlWithPerf(annotationMaskUrl, ws.id)
        .then(maskData => {
          if (kernel.current) {
            kernel.current.setWorkspace(ws.id, (workspace) => ({
              ...workspace,
              annotationMask: maskData,
              annotationMaskVersionId: activeVersionId,
              versions: (workspace.versions ?? []).map((version) => {
                if (version.id !== activeVersionId) return version;
                return {
                  ...version,
                  annotationMask: maskData,
                  annotationMaskIsDirty: false,
                };
              }),
            }), projectId);
          }
        })
        .catch(e => {
          console.error(`Failed to load annotation mask from ${annotationMaskUrl}`, e);
        });
    });

    if (targetUIWorkspaceId) {
      kernel.current.setProjectUIWorkspaceId(projectId, targetUIWorkspaceId);
    }

    const activeWorkspace = loadedWorkspaces.find(ws => ws.id === targetUIWorkspaceId);

    if (kernel.current && activeWorkspace?.frameRect){
      kernel.current.zoomToFitRectInViewport(projectId, activeWorkspace.frameRect, 1.2, kernel.current.getSpectacleMode());
    } else if (kernel.current && targetUIWorkspaceId) {
      kernel.current.zoomOnCanvas(targetUIWorkspaceId);
    }
  }, [kernel]);

  const createNewProject = useCallback(async (options?: { projectType?: ProjectType; artboard?: ArtboardSize; name?: string }): Promise<ProjectMetadata | null> => {
    if (!kernel.current) return null;

    try {
      const userContextUserId = kernel.current.getUserContext().dbUserId;

      // The project KIND (Canvas vs List) is fixed at creation — persist it on the
      // create itself so it can never be ambiguous. FREEFORM is the DB default.
      const created = await kernel.current.createEntity('Project', {
        ...(options?.projectType && options.projectType !== 'FREEFORM'
          ? { projectType: options.projectType }
          : {}),
        ...(options?.projectType === 'VECTOR' && options.artboard ? { artboard: options.artboard } : {}),
        ...(options?.name ? { name: options.name } : {}),
      });
      const newProjectId = created?.id;
      if (!newProjectId) {
        throw new Error('Project creation failed: missing project id.');
      }

      await kernel.current.syncEntity('Project', newProjectId).catch(() => undefined);
      const syncedProject = kernel.current.getState().inventory.Project.registry[newProjectId];
      const newProjectUserId = syncedProject?.userId ?? userContextUserId ?? undefined;

      await kernel.current.ensureSession({
        initialSubject: { entityType: 'Project', entityId: newProjectId },
      });

      if (syncedProject) {
        return {
          id: syncedProject.id,
          name: syncedProject.name,
          imageUrl: syncedProject.imageUrl,
          lastModified: syncedProject.updatedAt,
          totalCreditsSpent: syncedProject.totalCreditsSpent ?? 0,
          favorite: syncedProject.favorite ?? false,
          toDelete: syncedProject.toDelete ?? false,
          userId: syncedProject.userId,
        };
      }

      return {
        id: newProjectId,
        name: '',
        lastModified: new Date().toISOString(),
        totalCreditsSpent: 0,
        userId: newProjectUserId,
      };
    } catch (error: any) {
      console.error('Error creating project:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create project. Please try again.';
      kernel.current?.handleError(errorMessage);
      return null;
    }
  }, [kernel]);

  const createProjectAndQuickstart = useCallback(async (userId: string, prompt: string, imageUrls?: string[], options?: { projectType?: ProjectType; artboard?: ArtboardSize }) => {
    const newProjectMeta = await createNewProject(options);
    if (newProjectMeta) {
      const finalUserId = newProjectMeta.userId || userId || kernel.current?.getUserContext().dbUserId || '';
      openProject(newProjectMeta.id, finalUserId, prompt, imageUrls);
    }
  }, [createNewProject, openProject, kernel]);

  const sendImageToNewProject = useCallback(async (file: File, name: string) => {
    const k = kernel.current;
    if (!k) return;
    try {
      const project = await createNewProject({ name: name.trim().slice(0, 80) });
      if (!project) return;
      const loaded = waitForProjectLoad(k, project.id);
      loaded.catch(() => undefined);
      await k.focusEntityUI('Project', project.id);
      await loaded;
      const files = new DataTransfer();
      files.items.add(file);
      // No world position: the image fills the project's default empty workspace; a position would add a second one beside it.
      await k.addFilesAsLayers(files.files, 'project', undefined, { projectId: project.id });
      k.logUserAction('Image Sent To New Project', { projectId: project.id });
    } catch (error) {
      console.error('[ProjectManager] sendImageToNewProject failed:', error);
      k.handleError('Could not open the image in a new project.');
    }
  }, [kernel, createNewProject]);

  const startDealProject = useCallback(async (params: { 
    dealId: string; 
    prompt: string; 
    unitThumbnail?: string | null; 
    brandLogo?: string | null;
  }) => {
    if (!kernel.current) return;
    const { dealId, prompt, unitThumbnail, brandLogo } = params;
    
    const toastId = toast.loading("Initializing Deal Project...");
    try {
      const newProjectMeta = await createNewProject();
      if (!newProjectMeta) throw new Error("Failed to create project metadata");

      const projectId = newProjectMeta.id;
      const ownerId = newProjectMeta.userId || kernel.current.getUserContext().dbUserId;

      const [cloudUnitThumb, cloudBrandLogo] = await Promise.all([
        unitThumbnail ? ensureCloudUrl(unitThumbnail, ownerId) : Promise.resolve(null),
        brandLogo ? ensureCloudUrl(brandLogo, ownerId) : Promise.resolve(null)
      ]);

      if (dealId) {
        const deal = kernel.current.getLeasingDealMap()[dealId];
        const existingProjectIds = deal?.projectIds || [];
        
        if (!existingProjectIds.includes(projectId)) {
          await kernel.current.updateEntity('Deal', dealId, {
            projectIds: [...existingProjectIds, projectId]
          });
        }
      }

      const imagesForQuickstart = [];
      if (cloudUnitThumb) imagesForQuickstart.push(cloudUnitThumb);
      if (cloudBrandLogo) imagesForQuickstart.push(cloudBrandLogo);

      toast.success("Project ready", { id: toastId });
      openProject(projectId, ownerId || '', prompt, imagesForQuickstart);

    } catch (error: any) {
      console.error("[ProjectManager] startDealProject failed:", error);
      toast.error(error.message || "Failed to initialize deal project", { id: toastId });
    }
  }, [kernel, createNewProject, openProject]);

  const buildProjectMetadataFromInventory = useCallback((projectId: string): ProjectMetadata | null => {
    const k = kernel.current;
    if (!k) return null;
    const projectRow = k.getState().inventory.Project.registry[projectId];
    if (!projectRow) return null;
    const name = typeof projectRow.name === 'string' ? projectRow.name.trim() : '';
    if (!name) return null;
    const totalCreditsSpentRaw = projectRow.totalCreditsSpent;
    const totalCreditsSpent =
      typeof totalCreditsSpentRaw === 'number' && Number.isFinite(totalCreditsSpentRaw)
        ? totalCreditsSpentRaw
        : 0;

    return {
      id: projectId,
      name,
      imageUrl: typeof projectRow.imageUrl === 'string' ? projectRow.imageUrl : null,
      totalCreditsSpent,
      favorite: projectRow.favorite === true,
      toDelete: projectRow.toDelete === true,
      userId: typeof projectRow.userId === 'string' ? projectRow.userId : undefined,
    };
  }, [kernel]);

  const buildProjectSkeletonFromEntity = useCallback((projectId: string): Project | null => {
    const k = kernel.current;
    if (!k) return null;
    const mergedProject = k.readEntityKernelMergedState('Project', projectId);
    if (!isRecord(mergedProject)) return null;

    return {
      actionHistory: Array.isArray(mergedProject.actionHistory)
        ? (mergedProject.actionHistory as Project['actionHistory'])
        : [],
      workspaceCounter:
        typeof mergedProject.workspaceCounter === 'number' && Number.isFinite(mergedProject.workspaceCounter)
          ? mergedProject.workspaceCounter
          : 1,
      cloudAssetRegistry: Array.isArray(mergedProject.cloudAssetRegistry)
        ? (mergedProject.cloudAssetRegistry as Project['cloudAssetRegistry'])
        : [],
      layers: [],
      workspaces: [],
    };
  }, [kernel]);

  const handleNormalProjectLoad = useCallback(async (
    projectId: string,
    loadToken: number
  ): Promise<boolean> => {
    const k = kernel.current;
    if (!k) return false;

    const projectMetadata = buildProjectMetadataFromInventory(projectId);
    if (!projectMetadata) {
      return false;
    }

    const projectSkeleton = buildProjectSkeletonFromEntity(projectId);
    if (!projectSkeleton) {
      return false;
    }

    const hydratedStudioScene = await hydrateStudioSceneFromDatabase(projectId);
    if (!hydratedStudioScene) {
      return false;
    }

    if (latestProjectLoadTokenRef.current !== loadToken) {
      return true;
    }

    const actualProject: Project = {
      ...projectSkeleton,
      layers: hydratedStudioScene.layers.map((layer) => ({ ...layer })),
      workspaces: hydratedStudioScene.workspaces.map((workspace) => ({
        ...workspace,
        versions: Array.isArray(workspace.versions)
          ? workspace.versions.map((version) => ({ ...version }))
          : workspace.versions,
      })),
    };

    await handleProjectLoad(projectId, actualProject);
    return true;
  }, [
    kernel,
    latestProjectLoadTokenRef,
    buildProjectMetadataFromInventory,
    buildProjectSkeletonFromEntity,
    hydrateStudioSceneFromDatabase,
    handleProjectLoad,
  ]);

  const handleLegacyProjectLoad = useCallback(async (projectId: string, loadToken: number, ownerId?: string) => {
    if (!kernel.current) return;
    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    const projectOwnerIdFromRegistry = normalizedProjectId
      ? kernel.current.getInventory().Project.registry[normalizedProjectId]?.userId
      : undefined;

    const metadataPayload: LoadMetadataRequestBody = {
      action: 'loadMetadata',
      projectId,
      ownerId: ownerId || projectOwnerIdFromRegistry || undefined
    };
    const metadataPromise = axios.post('/api/projects', metadataPayload);

    const metadataResponse = await metadataPromise;
    const { projectMetadata } = metadataResponse.data;
    if (latestProjectLoadTokenRef.current !== loadToken) {
      return;
    }
    if (!projectMetadata || !projectMetadata.name) {
        kernel.current?.handleError("Failed to load project metadata. Cannot fetch project content.");
        return;
    }
    const finalOwnerId = projectMetadata.userId || ownerId;
    let contentResponse;

    if (finalOwnerId) {
      const newEndpointUrl = `/api/assets/${finalOwnerId}/${projectId}.ftr`;
      contentResponse = await fetch(newEndpointUrl);

      if (contentResponse.status === 404) {
        console.log(`[Project Load] FTR file not found at new endpoint (${newEndpointUrl}). Falling back to deprecated endpoint.`);
        const oldEndpointUrl = `/api/user-files/${projectId}.ftr`;
        contentResponse = await fetch(oldEndpointUrl);
      }
    } else {
      console.warn(`[Project Load] Owner ID (userId) not found in metadata for project ${projectId}. Using deprecated endpoint directly.`);
      contentResponse = await fetch(`/api/user-files/${projectId}.ftr`);
    }

    if (!contentResponse.ok) {
      if (contentResponse.status === 404) {
        // No .ftr blob — but DB-native projects (everything created since the
        // inventory-first flow, notably LIST projects) never have one. The DB
        // is the source of truth: loading empty is only correct when the DB
        // scene is empty too, otherwise a project with real outputs would
        // silently open blank.
        if (latestProjectLoadTokenRef.current !== loadToken) {
          return;
        }
        const dbSceneForFtrless = await hydrateStudioSceneFromDatabase(projectId);
        if (latestProjectLoadTokenRef.current !== loadToken) {
          return;
        }
        if (dbSceneForFtrless) {
          const dbProject: Project = {
            actionHistory: [],
            layers: dbSceneForFtrless.layers.map((layer) => ({ ...layer, imageData: null })),
            workspaces: dbSceneForFtrless.workspaces.map((workspace) => ({
              ...workspace,
              versions: Array.isArray(workspace.versions)
                ? workspace.versions.map((version) => ({ ...version }))
                : workspace.versions,
            })),
          };
          await handleProjectLoad(projectId, dbProject);
          return;
        }
        if (latestProjectLoadTokenRef.current !== loadToken) {
          return;
        }
        // Les quatre chemins de creation posent un workspace en base. Sans scene ici, le
        // projet n'est donc pas vide : l'hydratation a echoue, ou la ligne est anormale.
        kernel.current?.handleError('Failed to load project scene. Please retry.');
      } else {
        kernel.current?.handleError(`Failed to load project content: ${contentResponse.status} ${contentResponse.statusText}`);
      }
      return;
    }
    const contentText = await contentResponse.text();

    let projectContent: any = null;
    try {
      projectContent = JSON.parse(contentText);
    } catch (parseError: any) {
      console.error('Failed to parse project JSON:', parseError);
      kernel.current?.handleError('Failed to parse project file. Please try again or contact support.');
      return;
    }

    if (projectContent) {
      const hydratedStudioScene = await hydrateStudioSceneFromDatabase(projectId);
      if (hydratedStudioScene) {
        projectContent.layers = hydratedStudioScene.layers.map((layer) => ({ ...layer }));
        projectContent.workspaces = hydratedStudioScene.workspaces.map((workspace) => ({
          ...workspace,
          versions: Array.isArray(workspace.versions)
            ? workspace.versions.map((version) => ({ ...version }))
            : workspace.versions,
        }));
      } else {
        const normalizedLegacyScene = normalizeLegacyProjectSceneForStrictLoad(
          normalizedProjectId || projectId,
          projectContent as Record<string, unknown>
        );
        projectContent.layers = normalizedLegacyScene.layers;
        projectContent.workspaces = normalizedLegacyScene.workspaces;
      }

      const actualProject: Project = {
        ...projectContent,
        layers: [ ...(projectContent.layers ? projectContent.layers.map((layer: Layer) => ({ ...layer, imageData: null })) : []) ]
      };
      if (latestProjectLoadTokenRef.current !== loadToken) {
        return;
      }
      await handleProjectLoad(projectId, actualProject);
    } else {
      kernel.current?.handleError('Empty project file.');
    }
  }, [kernel, handleProjectLoad, hydrateStudioSceneFromDatabase]);

  const loadProject = useCallback(async (projectId: string, ownerId?: string) => {
    if (!kernel.current) return;
    // Every in-place Canvas open (click, URL, conversation, agent) mounts ProjectPage, which syncs the row and loads here.
    // A List lands here only when its row was not hydrated before render, and is not a step.
    if (resolveProjectGenerationViewMode(kernel.current.getInventory().Project.registry[projectId]?.projectType) === 'studio') {
      const previousProjectId = shownCanvasProjectIdRef.current;
      shownCanvasProjectIdRef.current = projectId;
      if (previousProjectId && previousProjectId !== projectId) {
        // Moving the ref first makes the load this reopen triggers record nothing, so the redo stack survives.
        const reopen = (id: string) => {
          shownCanvasProjectIdRef.current = id;
          return kernel.current?.focusEntityUI('Project', id);
        };
        // executeAction runs redo at once; this load already opens projectId, so that first run must not focus it again.
        let opening = true;
        kernel.current.executeAction({
          description: `Open project ${projectId}`,
          undo: () => reopen(previousProjectId),
          redo: () => {
            if (opening) {
              opening = false;
              return;
            }
            return reopen(projectId);
          },
        });
      }
    }
    const loadToken = latestProjectLoadTokenRef.current + 1;
    latestProjectLoadTokenRef.current = loadToken;
    kernel.current.clearAssetLoadingFailsafe();
    kernel.current.patchProjectRuntimeState(projectId, {
      isProjectLoading: true,
      essentialAssetsReady: false,
    });
    kernel.current.setPendingAssetUrls(() => new Set());

    try {
      let loadedFromNormal = false;
      loadedFromNormal = await handleNormalProjectLoad(projectId, loadToken).catch((error) => {
        // Pas de toast : le chargeur legacy prend la suite et peut aboutir. Mais un echec
        // ici est la premiere cause d'un repli legacy, donc il doit laisser une trace.
        console.error(`[Project Load] Database-native load failed for project ${projectId}, falling back to legacy.`, error);
        return false;
      });

      if (!loadedFromNormal) {
        await handleLegacyProjectLoad(projectId, loadToken, ownerId);
      }
    } finally {
      kernel.current?.patchProjectRuntimeState(projectId, { isProjectLoading: false });
    }
  }, [kernel, latestProjectLoadTokenRef, handleProjectLoad, handleNormalProjectLoad, handleLegacyProjectLoad]);

  const downloadProjectStory = useCallback(async (projectId: string) => {
    const kernelHandle = kernel.current;
    if (!kernelHandle) {
      console.error('Editor not ready.');
      return;
    }

    const targetProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!targetProjectId) {
      toast.error('Cannot generate Project Story: missing projectId.');
      return;
    }

    kernelHandle.logUserAction?.('Project Story Download Requested', {
      source: 'Sidebar Menu',
      projectId: targetProjectId,
    });
    const toastId = toast.loading('Generating Project Story...');

    try {
      const projectName = kernelHandle.getInventory().Project.registry[targetProjectId]?.name || 'Project';

      const liveLayers = listProjectLayers(targetProjectId);
      const assetDataUriMap = new Map<string, string>();

      await Promise.all(
        liveLayers.map(async (layer) => {
          if (!layer.imageData || typeof layer.imageSrc !== 'string') {
            return;
          }
          const trimmedSrc = layer.imageSrc.trim();
          if (!trimmedSrc || assetDataUriMap.has(trimmedSrc)) {
            return;
          }
          try {
            const dataUrl = await imageDataToDataURL(layer.imageData, 'image/webp');
            assetDataUriMap.set(trimmedSrc, dataUrl);
          } catch (error) {
            console.warn(`[ProjectStory] Failed to convert in-memory layer ${layer.id} to data URL.`, error);
          }
        }),
      );

      const sanitizedLayers = liveLayers.map((layer) => {
        const { imageData, ...rest } = layer;
        void imageData;
        return rest;
      });

      const sanitizedWorkspaces = listProjectWorkspaces(targetProjectId).map(sanitizeWorkspaceForPersistence);

      const mergedProjectState = kernelHandle.readProjectKernelMergedState(targetProjectId);
      const snapshotProject: Project = {
        ...(isRecord(mergedProjectState) ? (mergedProjectState as Partial<Project>) : {}),
        activeLayerWorkspaceId: kernelHandle.getProjectUIWorkspaceId(targetProjectId),
        actionHistory: kernelHandle.getActionHistory(),
        layers: sanitizedLayers,
        workspaces: sanitizedWorkspaces,
        cloudAssetRegistry: kernelHandle.getCloudAssetRegistry(),
        generationTasks: kernelHandle.getGenerationTasks(targetProjectId),
      };

      delete snapshotProject.images;

      let snapshots: GenerationTaskSnapshot[] = [];
      try {
        const snapshotResponse = await axios.get(`/api/inventory/projects/${encodeURIComponent(targetProjectId)}/snapshots`);
        snapshots = Array.isArray(snapshotResponse.data?.snapshots)
          ? snapshotResponse.data.snapshots
          : [];
      } catch (e) {
        console.warn('Failed to fetch snapshots for story', e);
      }

      const analysis = runProjectSuccessForensics(snapshotProject, { projectName, snapshots });

      console.group('Project Story Generation Debug');
      console.log('Forensics Analysis Result:', analysis);

      const requiredAssetUrls = collectAnalysisAssetUrls(analysis);
      console.log('Required Asset URLs (from analysis):', Array.from(requiredAssetUrls));

      const missingUrls = Array.from(requiredAssetUrls).filter((url) => !assetDataUriMap.has(url));
      console.log('Missing URLs (not in memory):', missingUrls);

      if (missingUrls.length > 0) {
        await Promise.allSettled(
          missingUrls.map(async (url) => {
            const dataUrl = await fetchAssetAsDataURL(url);
            if (dataUrl) {
              assetDataUriMap.set(url, dataUrl);
            }
          }),
        );
      }

      console.log('Final Asset Map Size:', assetDataUriMap.size);
      console.log('Final Asset Map Keys:', Array.from(assetDataUriMap.keys()));
      console.groupEnd();

      const reportHtml = await buildProjectReportHtml({
        projectName,
        analysis,
        generatedAtIso: new Date().toISOString(),
        inlineImages: true,
        assetDataUriMap,
      });

      const blob = new Blob([reportHtml], { type: 'text/html;charset=utf-8' });
      const downloadUrl = URL.createObjectURL(blob);
      const safeBase = sanitizeFileName(projectName);
      const downloadFileName = `${safeBase}-project-story.html`;

      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = downloadFileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(downloadUrl);

      toast.success('Project Story downloaded.', { id: toastId });
    } catch (error) {
      console.error('Failed to generate Project Story.', error);
      toast.error('Failed to generate Project Story.', { id: toastId });
      kernelHandle.handleError?.('Could not generate the Project Story.');
    }
  }, [kernel, listProjectLayers, listProjectWorkspaces]);

  const buildProjectAssetReport = useCallback(async (
    projectId: string,
    timeline: UserAction[],
    userEmail: string,
  ): Promise<ProjectAssetReport | null> => {
    try {
      if (!projectId) {
        throw new Error('Missing projectId for report generation.');
      }
      const projectEntity = await kernel.current?.syncEntity('Project', projectId, { force: true });
      const totalCreditsUsed = Number(projectEntity?.totalCreditsSpent ?? 0);

      const registry = kernel.current?.getCloudAssetRegistry() || [];

      const assetsBySource = registry.reduce((acc, asset) => {
        acc[asset.source] = (acc[asset.source] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const potentiallyOrphanedAssets = registry.filter(
        asset => new Date(asset.lastSeenAt).getTime() <= new Date(asset.createdAt).getTime()
      ).length;

      const downloadEvents = timeline.filter(action => action.name.includes('Downloaded'));

      const firstAction = timeline[0];
      const lastAction = timeline[timeline.length - 1];

      return {
        reportGeneratedAt: new Date().toISOString(),
        projectId,
        userEmail,
        summary: {
          projectStartTime: firstAction ? firstAction.timestamp : 'N/A',
          projectEndTime: lastAction ? lastAction.timestamp : 'N/A',
          totalCreditsUsed,
          totalCloudAssets: registry.length,
          potentiallyOrphanedAssets,
          assetsBySource: {
            generation: assetsBySource['generation'] || 0,
            upload: assetsBySource['upload'] || 0,
            snapshot: assetsBySource['snapshot'] || 0,
            mask: assetsBySource['mask'] || 0,
          },
          totalDownloads: downloadEvents.length,
        },
        // Preserve the raw history to keep the event timeline authoritative.
        timeline,
      };
    } catch (error) {
      console.error('Failed to build the Cloud Asset Report.', error);
      return null;
    }
  }, [kernel]);

  return useMemo(() => ({
    deleteProject,
    copyProject,
    adminCopyProjectToSelf,
    updateProjectDto,
    renameProject,
    createProjectAndQuickstart,
    sendImageToNewProject,
    startDealProject,
    openProject,
    loadProject,
    resyncOpenStudioScene,
    downloadProjectStory,
    buildProjectAssetReport
  }), [
    deleteProject,
    copyProject,
    adminCopyProjectToSelf,
    updateProjectDto,
    renameProject,
    createProjectAndQuickstart,
    sendImageToNewProject,
    startDealProject,
    openProject,
    loadProject,
    resyncOpenStudioScene,
    downloadProjectStory,
    buildProjectAssetReport
  ]);
};
